import React, { useState } from "react";
import { motion } from "framer-motion";
import UserAvatar from "../UserAvatar";
import {
  Plus,
  RefreshCw,
  Edit2,
  Trash2,
  Coins,
  ChevronLeft,
  ChevronRight,
  Search,
  X,
  User,
  Mail,
  AlertCircle,
  CheckCircle2,
  Loader2
} from "lucide-react";
import axios from "axios";
import { useTranslation } from "../../i18n";

const API_URL = process.env.REACT_APP_API_URL || "";

// Modal Component
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative w-full max-w-lg bg-white rounded-2xl shadow-xl p-6 z-10"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {children}
        </motion.div>
      </div>
    </div>
  );
};

const UsersSection = ({
  users = [],
  usersCount = 0,
  isLoading = false,
  error = null,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  onRefresh,
  searchQuery = "",
  onSearchChange,
}) => {
  const { t } = useTranslation("admin_users");
  const ROLE_LABEL_KEYS = { admin: "users.roles.admin", user: "users.roles.user" };
  const roleLabel = (role) => t(ROLE_LABEL_KEYS[role] || ROLE_LABEL_KEYS.user, { defaultValue: role });
  const deleteConfirmParts = t("users.delete_modal.confirm").split("{{name}}");
  const adjustingForParts = t("users.adjust_modal.adjusting_for").split("{{name}}");
  const [searchTerm, setSearchTerm] = useState(searchQuery);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAdjustPointsModalOpen, setIsAdjustPointsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState(null);

  // Add User Form State
  const [addForm, setAddForm] = useState({
    email: "",
    full_name: "",
    password: "",
    confirmPassword: "",
    is_admin: false,
    is_active: true,
    job_title: "",
    department: "",
    location: "",
  });

  // Edit User Form State
  const [editForm, setEditForm] = useState({
    email: "",
    full_name: "",
    password: "",
    is_admin: false,
    is_active: true,
    job_title: "",
    department: "",
    location: "",
  });

  // Adjust Points Form State
  const [adjustPointsForm, setAdjustPointsForm] = useState({
    amount: "",
    reason: "",
  });

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const getToken = () => localStorage.getItem("token");

  // Handle search with debounce
  const handleSearchInput = (value) => {
    setSearchTerm(value);

    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Debounce the search - wait 300ms after user stops typing
    const timeout = setTimeout(() => {
      onSearchChange?.(value);
    }, 300);

    setSearchTimeout(timeout);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm("");
    onSearchChange?.("");
  };

  // Sync local state with prop when it changes externally
  React.useEffect(() => {
    setSearchTerm(searchQuery);
  }, [searchQuery]);

  // Add User Handler
  const handleAddUser = async (e) => {
    e.preventDefault();
    if (addForm.password !== addForm.confirmPassword) {
      showNotification(t("users.toasts.passwords_mismatch"), "error");
      return;
    }
    if (addForm.password.length < 8) {
      showNotification(t("users.toasts.password_min_8"), "error");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/v1/users/`,
        {
          email: addForm.email.trim(),
          full_name: addForm.full_name.trim(),
          password: addForm.password,
          role: addForm.is_admin ? "admin" : "user",
          is_active: addForm.is_active,
          job_title: addForm.job_title.trim() || undefined,
          department: addForm.department.trim() || undefined,
          location: addForm.location.trim() || undefined,
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      showNotification(t("users.toasts.user_created"));
      setIsAddModalOpen(false);
      setAddForm({
        email: "",
        full_name: "",
        password: "",
        confirmPassword: "",
        is_admin: false,
        is_active: true,
        job_title: "",
        department: "",
        location: "",
      });
      onRefresh?.();
    } catch (error) {
      showNotification(
        error.response?.data?.detail || t("users.toasts.create_failed"),
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Edit User Handler
  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;

    setIsSubmitting(true);
    try {
      const payload = {
        email: editForm.email.trim(),
        full_name: editForm.full_name.trim(),
        role: editForm.is_admin ? "admin" : "user",
        is_active: editForm.is_active,
        job_title: editForm.job_title.trim() || undefined,
        department: editForm.department.trim() || undefined,
        location: editForm.location.trim() || undefined,
      };

      if (editForm.password) {
        if (editForm.password.length < 8) {
          showNotification(t("users.toasts.password_min_8"), "error");
          setIsSubmitting(false);
          return;
        }
        payload.password = editForm.password;
      }

      await axios.patch(`${API_URL}/api/v1/users/${selectedUser.id}`, payload, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      showNotification(t("users.toasts.user_updated"));
      setIsEditModalOpen(false);
      onRefresh?.();
    } catch (error) {
      showNotification(
        error.response?.data?.detail || t("users.toasts.update_failed"),
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete User Handler
  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    setIsSubmitting(true);
    try {
      await axios.delete(`${API_URL}/api/v1/users/${selectedUser.id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      showNotification(t("users.toasts.user_deleted"));
      setIsDeleteModalOpen(false);
      onRefresh?.();
    } catch (error) {
      showNotification(
        error.response?.data?.detail || t("users.toasts.delete_failed"),
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Adjust Points Handler
  const handleAdjustPoints = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;

    const amount = parseInt(adjustPointsForm.amount, 10);
    if (!amount || amount === 0) {
      showNotification(t("users.toasts.amount_nonzero"), "error");
      return;
    }

    setIsSubmitting(true);
    try {
      // Backend expects query params, not JSON body
      const params = new URLSearchParams({ amount: amount.toString() });
      if (adjustPointsForm.reason.trim()) {
        params.append("reason", adjustPointsForm.reason.trim());
      }

      await axios.post(
        `${API_URL}/api/v1/wallet/admin/adjust/${selectedUser.id}?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      showNotification(
        amount > 0 ? t("users.toasts.points_added") : t("users.toasts.points_deducted")
      );
      setIsAdjustPointsModalOpen(false);
      setAdjustPointsForm({ amount: "", reason: "" });
      onRefresh?.();
    } catch (error) {
      showNotification(
        error.response?.data?.detail || t("users.toasts.adjust_failed"),
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setEditForm({
      email: user.email || "",
      full_name: user.full_name || "",
      password: "",
      is_admin: user.role === "admin",
      is_active: user.is_active !== false,
      job_title: user.job_title || "",
      department: user.department || "",
      location: user.location || "",
    });
    setIsEditModalOpen(true);
  };

  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  const openAdjustPointsModal = (user) => {
    setSelectedUser(user);
    setAdjustPointsForm({ amount: "", reason: "" });
    setIsAdjustPointsModalOpen(true);
  };

  return (
    <div className="p-3 sm:p-6">
      {/* Notification */}
      {notification && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-2xl shadow-lg ${
            notification.type === "error"
              ? "bg-red-500 text-white"
              : "bg-green-500 text-white"
          }`}
        >
          {notification.type === "error" ? (
            <AlertCircle className="w-5 h-5" />
          ) : (
            <CheckCircle2 className="w-5 h-5" />
          )}
          {notification.message}
        </motion.div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between gap-2 mb-4 sm:mb-6">
        <div className="min-w-0">
          <h2 className="text-base sm:text-xl font-semibold text-gray-900">{t("users.title")}</h2>
          <p className="text-xs sm:text-sm text-gray-500">
            {t("users.total_users", { count: usersCount })}
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <button
            onClick={onRefresh}
            disabled={isLoading}
            className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors text-sm"
            title={t("common:actions.refresh")}
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">{t("common:actions.refresh")}</span>
          </button>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 org-gradient org-btn text-white rounded-full transition-opacity text-sm"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">{t("users.add_user")}</span>
            <span className="sm:hidden">{t("common:actions.add")}</span>
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder={t("users.search_placeholder")}
          value={searchTerm}
          onChange={(e) => handleSearchInput(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
        {searchTerm && (
          <button
            onClick={handleClearSearch}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-2xl text-red-700">
          {error}
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          {searchTerm ? t("users.empty_search") : t("users.empty")}
        </div>
      ) : (
        <>
          {/* Users Table — desktop only */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-600">
                    {t("users.columns.user")}
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">
                    {t("users.columns.role")}
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">
                    {t("users.columns.status")}
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">
                    {t("users.columns.department")}
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-600">
                    {t("users.columns.points")}
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-600">
                    {t("users.columns.actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr
                    key={user.id}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <UserAvatar
                          src={user.avatar_url}
                          name={user.full_name || user.email}
                          className="w-10 h-10 rounded-full object-cover"
                          textClassName="text-sm"
                        />
                        <div>
                          <div className="font-medium text-gray-900">
                            {user.full_name || "—"}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.role === "admin"
                            ? "bg-purple-100 text-purple-700"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {roleLabel(user.role)}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.is_active !== false
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {user.is_active !== false ? t("common:status.active") : t("common:status.inactive")}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-gray-600">
                      {user.department || "—"}
                    </td>
                    <td className="py-4 px-4 text-right">
                      <span className="inline-flex items-center gap-1 text-yellow-600 font-medium">
                        <Coins className="w-4 h-4" />
                        {user.points_balance ?? 0}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openAdjustPointsModal(user)}
                          className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-full transition-colors"
                          title={t("users.actions.adjust_points")}
                        >
                          <Coins className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openEditModal(user)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                          title={t("users.actions.edit_user")}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openDeleteModal(user)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                          title={t("users.actions.delete_user")}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Users cards — mobile only */}
          <div className="md:hidden space-y-2">
            {users.map((user) => (
              <div
                key={user.id}
                className="bg-white border border-gray-200 rounded-2xl p-3 space-y-2"
              >
                <div className="flex items-center gap-2">
                  <UserAvatar
                    src={user.avatar_url}
                    name={user.full_name || user.email}
                    className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                    textClassName="text-sm"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {user.full_name || "—"}
                    </div>
                    <div className="text-xs text-gray-500 truncate">{user.email}</div>
                  </div>
                  <span className="inline-flex items-center gap-0.5 text-yellow-600 font-medium text-xs flex-shrink-0">
                    <Coins className="w-3.5 h-3.5" />
                    {user.points_balance ?? 0}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 text-xs">
                  <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium ${
                        user.role === "admin"
                          ? "bg-purple-100 text-purple-700"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {roleLabel(user.role)}
                    </span>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium ${
                        user.is_active !== false
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {user.is_active !== false ? t("common:status.active") : t("common:status.inactive")}
                    </span>
                    {user.department && (
                      <span className="text-gray-500 truncate">{user.department}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <button
                      onClick={() => openAdjustPointsModal(user)}
                      className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded-full transition-colors"
                      title={t("users.actions.adjust_points")}
                      aria-label={t("users.actions.adjust_points_aria")}
                    >
                      <Coins className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => openEditModal(user)}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                      title={t("users.actions.edit_user")}
                      aria-label={t("users.actions.edit_user_aria")}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => openDeleteModal(user)}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                      title={t("users.actions.delete_user")}
                      aria-label={t("users.actions.delete_user_aria")}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-500">
                {t("users.page_of", { current: currentPage, total: totalPages })}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onPageChange?.(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let page;
                  if (totalPages <= 5) {
                    page = i + 1;
                  } else if (currentPage <= 3) {
                    page = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    page = totalPages - 4 + i;
                  } else {
                    page = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={page}
                      onClick={() => onPageChange?.(page)}
                      className={`w-10 h-10 rounded-full font-medium transition-colors ${
                        currentPage === page
                          ? "org-gradient text-white"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  onClick={() => onPageChange?.(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Add User Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title={t("users.add_modal.title")}
      >
        <form onSubmit={handleAddUser} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("users.form.email")} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                required
                value={addForm.email}
                onChange={(e) =>
                  setAddForm({ ...addForm, email: e.target.value })
                }
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder={t("users.form.email_placeholder")}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("users.form.full_name")} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                required
                value={addForm.full_name}
                onChange={(e) =>
                  setAddForm({ ...addForm, full_name: e.target.value })
                }
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder={t("users.form.full_name_placeholder")}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("users.form.password")} <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                required
                minLength={8}
                value={addForm.password}
                onChange={(e) =>
                  setAddForm({ ...addForm, password: e.target.value })
                }
                className="w-full px-4 py-2.5 border border-gray-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder={t("users.form.password_placeholder")}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("users.form.confirm_password")} <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                required
                value={addForm.confirmPassword}
                onChange={(e) =>
                  setAddForm({ ...addForm, confirmPassword: e.target.value })
                }
                className="w-full px-4 py-2.5 border border-gray-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder={t("users.form.confirm_password_placeholder")}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("users.form.job_title")}
              </label>
              <input
                type="text"
                value={addForm.job_title}
                onChange={(e) =>
                  setAddForm({ ...addForm, job_title: e.target.value })
                }
                className="w-full px-4 py-2.5 border border-gray-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder={t("users.form.job_title_placeholder")}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("users.form.department")}
              </label>
              <input
                type="text"
                value={addForm.department}
                onChange={(e) =>
                  setAddForm({ ...addForm, department: e.target.value })
                }
                className="w-full px-4 py-2.5 border border-gray-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder={t("users.form.department_placeholder")}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("users.form.location")}
            </label>
            <input
              type="text"
              value={addForm.location}
              onChange={(e) =>
                setAddForm({ ...addForm, location: e.target.value })
              }
              className="w-full px-4 py-2.5 border border-gray-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder={t("users.form.location_placeholder")}
            />
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={addForm.is_admin}
                onChange={(e) =>
                  setAddForm({ ...addForm, is_admin: e.target.checked })
                }
                className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700">{t("users.form.org_administrator")}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={addForm.is_active}
                onChange={(e) =>
                  setAddForm({ ...addForm, is_active: e.target.checked })
                }
                className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700">{t("users.form.active")}</span>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => setIsAddModalOpen(false)}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            >
              {t("common:actions.cancel")}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2 px-4 py-2 org-gradient org-btn text-white rounded-full transition-opacity disabled:opacity-50"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {t("users.add_modal.submit")}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title={t("users.edit_modal.title")}
      >
        <form onSubmit={handleEditUser} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("users.form.email")} <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              required
              value={editForm.email}
              onChange={(e) =>
                setEditForm({ ...editForm, email: e.target.value })
              }
              className="w-full px-4 py-2.5 border border-gray-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("users.form.full_name")} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={editForm.full_name}
              onChange={(e) =>
                setEditForm({ ...editForm, full_name: e.target.value })
              }
              className="w-full px-4 py-2.5 border border-gray-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("users.form.new_password")}
            </label>
            <input
              type="password"
              value={editForm.password}
              onChange={(e) =>
                setEditForm({ ...editForm, password: e.target.value })
              }
              className="w-full px-4 py-2.5 border border-gray-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder={t("users.form.password_placeholder")}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("users.form.job_title")}
              </label>
              <input
                type="text"
                value={editForm.job_title}
                onChange={(e) =>
                  setEditForm({ ...editForm, job_title: e.target.value })
                }
                className="w-full px-4 py-2.5 border border-gray-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("users.form.department")}
              </label>
              <input
                type="text"
                value={editForm.department}
                onChange={(e) =>
                  setEditForm({ ...editForm, department: e.target.value })
                }
                className="w-full px-4 py-2.5 border border-gray-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("users.form.location")}
            </label>
            <input
              type="text"
              value={editForm.location}
              onChange={(e) =>
                setEditForm({ ...editForm, location: e.target.value })
              }
              className="w-full px-4 py-2.5 border border-gray-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={editForm.is_admin}
                onChange={(e) =>
                  setEditForm({ ...editForm, is_admin: e.target.checked })
                }
                className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700">{t("users.form.org_administrator")}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={editForm.is_active}
                onChange={(e) =>
                  setEditForm({ ...editForm, is_active: e.target.checked })
                }
                className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700">{t("users.form.active")}</span>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => setIsEditModalOpen(false)}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            >
              {t("common:actions.cancel")}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2 px-4 py-2 org-gradient org-btn text-white rounded-full transition-opacity disabled:opacity-50"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {t("common:actions.save_changes")}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete User Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title={t("users.delete_modal.title")}
      >
        <div className="space-y-4">
          <div className="p-4 bg-red-50 border border-red-200 rounded-2xl">
            <p className="text-red-700">
              {deleteConfirmParts[0]}<strong>{selectedUser?.full_name || selectedUser?.email}</strong>{deleteConfirmParts[1]}
            </p>
            <p className="text-sm text-red-600 mt-2">
              {t("users.delete_modal.warning")}
            </p>
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={() => setIsDeleteModalOpen(false)}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            >
              {t("common:actions.cancel")}
            </button>
            <button
              onClick={handleDeleteUser}
              disabled={isSubmitting}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {t("users.delete_modal.submit")}
            </button>
          </div>
        </div>
      </Modal>

      {/* Adjust Points Modal */}
      <Modal
        isOpen={isAdjustPointsModalOpen}
        onClose={() => setIsAdjustPointsModalOpen(false)}
        title={t("users.adjust_modal.title")}
      >
        <form onSubmit={handleAdjustPoints} className="space-y-4">
          <div className="p-4 bg-gray-50 rounded-2xl">
            <p className="text-sm text-gray-600">
              {adjustingForParts[0]}<strong>{selectedUser?.full_name || selectedUser?.email}</strong>{adjustingForParts[1]}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("users.adjust_modal.amount")} <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              required
              value={adjustPointsForm.amount}
              onChange={(e) =>
                setAdjustPointsForm({
                  ...adjustPointsForm,
                  amount: e.target.value,
                })
              }
              className="w-full px-4 py-2.5 border border-gray-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder={t("users.adjust_modal.amount_placeholder")}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t("users.adjust_modal.amount_help")}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("users.adjust_modal.reason")}
            </label>
            <textarea
              value={adjustPointsForm.reason}
              onChange={(e) =>
                setAdjustPointsForm({
                  ...adjustPointsForm,
                  reason: e.target.value,
                })
              }
              className="w-full px-4 py-2.5 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              rows={3}
              placeholder={t("users.adjust_modal.reason_placeholder")}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => setIsAdjustPointsModalOpen(false)}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            >
              {t("common:actions.cancel")}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2 px-4 py-2 org-gradient org-btn text-white rounded-full transition-opacity disabled:opacity-50"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {t("users.adjust_modal.submit")}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default UsersSection;
